/**
 * A content node's ID is a `base:version` pair — `greeting` names the base node,
 * `greeting:en` its `en` version.
 *
 * These rules used to be spread across a computed property with side effects,
 * three editor methods and two template bindings. That let an untouched version
 * field read as an instruction to drop the version, silently renaming
 * `action_x_outcome:success` to `action_x_outcome` and queueing it for approval
 * (KR-11003).
 */

export const VERSION_SEPARATOR = ":";

export interface ContentNodeIdParts {
  baseId: string;
  versionId: string;
}

/**
 * Splits an ID into its base and version halves. Anything that is not a
 * non-empty string has no parts. A version containing further separators is
 * kept whole, so that splitting and composing round-trip.
 */
export function splitContentNodeId(id: unknown): ContentNodeIdParts {
  if (typeof id !== "string" || !id) {
    return { baseId: "", versionId: "" };
  }

  const [baseId, ...rest] = id.split(VERSION_SEPARATOR);
  return { baseId, versionId: rest.join(VERSION_SEPARATOR) };
}

/** Joins the two halves back into an ID. A node without a version is just its base. */
export function composeContentNodeId({ baseId, versionId }: ContentNodeIdParts): string {
  if (!baseId) {
    return "";
  }
  return versionId ? `${baseId}${VERSION_SEPARATOR}${versionId}` : baseId;
}

export interface ContentNodeRenameRequest {
  /** The ID the node currently has. */
  currentId: string;
  /** The base ID as typed into the editor. */
  newBaseId: string;
  /** The version ID as typed into the editor. Only consulted when it is being edited. */
  newVersionId: string;
  /** Whether the version field is actually open for editing. */
  isEditingVersion: boolean;
}

export type ContentNodeRenameResolution =
  | { status: "rename"; newId: string }
  | { status: "unchanged" }
  | { status: "invalid"; reason: string };

/**
 * Decides what a rename request means.
 *
 * The version is taken from the form only when the version field is open. A
 * closed field is not an instruction — it carries whatever the node already
 * has. Emptying an open version field on a versioned node is rejected rather
 * than treated as "drop the version", because collapsing a version onto its
 * base silently destroys the routing that points at it.
 */
export function resolveContentNodeRename({
  currentId,
  newBaseId,
  newVersionId,
  isEditingVersion,
}: ContentNodeRenameRequest): ContentNodeRenameResolution {
  const current = splitContentNodeId(currentId);
  const baseId = (newBaseId || "").trim();

  if (!baseId) {
    return { status: "invalid", reason: "Base ID cannot be empty" };
  }

  const typedVersionId = (newVersionId || "").trim();
  const versionId = isEditingVersion ? typedVersionId : current.versionId;

  if (isEditingVersion && current.versionId && !versionId) {
    return { status: "invalid", reason: "Version ID cannot be empty" };
  }

  const newId = composeContentNodeId({ baseId, versionId });

  if (!newId || newId === currentId) {
    return { status: "unchanged" };
  }

  return { status: "rename", newId };
}
